import { CaretDownOutlined } from "@ant-design/icons";
import { Avatar, Input, Tooltip, Tree } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { removeDiacritics } from "src/lib/stringsUtils";
import { useLocation } from "react-router-dom"; // ✅ thêm dòng này

const { Search } = Input;

export const MemoTooltip = Tooltip || React.memo(Tooltip);

const getParentKey = (key, tree) => {
  for (const node of tree) {
    if (node.children) {
      if (node.children.some(child => child.key === key)) {
        return node.key;
      }
      const parentKey = getParentKey(key, node.children);
      if (parentKey) return parentKey;
    }
  }
  return null;
};

const SearchTree = ({
  selectedNode,
  setSelectedNote = () => {},
  treeDataDefault = [],
  dataList = [],
  onSelectUnknown = () => {}, // ✅ callback khi click “Chưa có đơn vị”
}) => {
  const [expandedKeys, setExpandedKeys] = useState(
    dataList.map(item => item.key)
  );
  const [searchValue, setSearchValue] = useState("");
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const location = useLocation(); // ✅ lấy location từ react-router
  const [hovered, setHovered] = useState(false); // ✅ quản lý hover
  const [isSelectedUnknown, setIsSelectedUnknown] = useState(false); // ✅ quản lý trạng thái chọn

  const onExpand = newExpandedKeys => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onChange = e => {
    const { value } = e.target;
    const textSearch = removeDiacritics(value.toLowerCase());

    const findMatchingNodes = (data, matchingKeys = new Set()) => {
      data.forEach(item => {
        const textMatch =
          !textSearch || item.removeDiacriticsTitle.indexOf(textSearch) > -1;

        if (textMatch) {
          matchingKeys.add(item.key);
          const parentKey = getParentKey(item.key, treeDataDefault);
          if (parentKey) {
            matchingKeys.add(parentKey);
          }
        }

        if (item.children) {
          findMatchingNodes(item.children, matchingKeys);
        }
      });
      return matchingKeys;
    };

    const matchingKeys = findMatchingNodes(dataList);
    setExpandedKeys(Array.from(matchingKeys));
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const treeData = useMemo(() => {
    const textSearch = removeDiacritics(searchValue.toLowerCase());

    const loop = data =>
      data
        .map(item => {
          const strTitle = item.title || "";
          const index = item.removeDiacriticsTitle.indexOf(textSearch);
          const beforeStr = strTitle.substring(0, index);
          const midStr = strTitle.substring(index, index + textSearch.length);
          const afterStr = strTitle.slice(index + textSearch.length);

          const createName = () => {
            return index > -1 ? (
              <span>
                {beforeStr}
                <span
                  className="site-tree-search-value"
                  style={{
                    color: "#f10101",
                    backgroundColor: "#ffeb3b",
                  }}
                >
                  {midStr}
                </span>
                {afterStr} ({item?.MemberNo || 0})
              </span>
            ) : (
              <span>
                {strTitle} ({item?.MemberNo || 0})
              </span>
            );
          };

          const name = createName();

          const titleRender = (className, nameToRender) => (
            <div className={className || ""} style={{ padding: "3px 5px" }}>
              <Avatar
                size={24}
                src={
                  item?.Image ? (
                    <img src={item?.Image} alt="avatar" />
                  ) : undefined
                }
                style={{
                  backgroundColor: "#7265e6",
                  verticalAlign: "middle",
                  fontSize: "16px",
                }}
                className="mr-8"
              >
                {item?.AssociationName?.[0] || item?.title?.[0] || ""}
              </Avatar>
              <span>{nameToRender}</span>
            </div>
          );

          const title = (
            <MemoTooltip
              title={
                <div>
                  <div>{titleRender("", name)}</div>
                  <div style={{ fontSize: "12px" }}>
                    <strong>Số thành viên:</strong> {item?.MemberNo || 0}
                  </div>
                </div>
              }
              key={item.key}
            >
              {titleRender("max-line1", name)}
            </MemoTooltip>
          );

          const currentNodeMatch =
            !textSearch || item.removeDiacriticsTitle.indexOf(textSearch) > -1;

          if (item.children) {
            const filteredChildren = loop(item.children);
            const hasValidChildren = filteredChildren.length > 0;

            if (currentNodeMatch || hasValidChildren) {
              return { title, key: item.key, children: filteredChildren };
            }
            return null;
          }

          if (currentNodeMatch) {
            return { title, key: item.key };
          }

          return null;
        })
        .filter(Boolean);

    return loop(treeDataDefault);
  }, [searchValue, treeDataDefault]);

  useEffect(() => {
    if (!dataList?.length) return;

    const textSearch = removeDiacritics(searchValue.toLowerCase());

    const findMatchingNodes = (data, matchingKeys = new Set()) => {
      data.forEach(item => {
        const textMatch =
          !textSearch || item.removeDiacriticsTitle.indexOf(textSearch) > -1;

        if (textMatch) {
          matchingKeys.add(item.key);
          const parentKey = getParentKey(item.key, treeDataDefault);
          if (parentKey) {
            matchingKeys.add(parentKey);
          }
        }

        if (item.children) {
          findMatchingNodes(item.children, matchingKeys);
        }
      });
      return matchingKeys;
    };

    const matchingKeys = findMatchingNodes(dataList);
    setExpandedKeys(Array.from(matchingKeys));
  }, [dataList, searchValue, treeDataDefault]);

  const handleSelectUnknown = () => {
    setIsSelectedUnknown(true);
    onSelectUnknown();
  };

  return (
    <div>
      <div>
        <Search
          allowClear
          style={{ marginBottom: 8 }}
          placeholder="Tìm kiếm tên đơn vị"
          onChange={onChange}
          value={searchValue}
          enterButton
        />
      </div>

      {/* ✅ Chỉ hiển thị khi ở /hoi-vien-dang-ky */}
      {location.pathname === "/hoi-vien-dang-ky" && (
        <div
          style={{
            display: "flex",
            marginLeft: 40,
            alignItems: "center",
            cursor: "pointer",
            borderRadius: 8,
            marginBottom: 10,
            backgroundColor: hovered
              ? "#f5f5f5"
              : isSelectedUnknown
              ? "#e6f7ff"
              : "transparent",
            color: isSelectedUnknown ? "#1890ff" : "#333",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleSelectUnknown}
        >
          <Avatar
            size={25}
            style={{
              backgroundColor: "#7265e6",
              color: "#fff",
              fontSize: 20,
              marginRight: 6,
            }}
          >
            ?
          </Avatar>
          <div
            style={{
              fontSize: 17,
              fontWeight: 500,
              marginBottom: 3,
            }}
          >
            Chưa có đơn vị
          </div>
        </div>
      )}

      <Tree
        style={{ height: "calc(100vh - 230px)" }}
        showLine
        switcherIcon={
          <CaretDownOutlined style={{ fontSize: 16, marginTop: 5 }} />
        }
        defaultExpandAll
        onExpand={onExpand}
        expandedKeys={expandedKeys}
        autoExpandParent={autoExpandParent}
        treeData={treeData}
        defaultSelectedKeys={[0]}
        selectedKeys={[selectedNode?.key ?? 0]}
        onSelect={(_, e) => {
          setIsSelectedUnknown(false); // bỏ chọn khi click vào tree
          setSelectedNote(e?.node);
        }}
      />
    </div>
  );
};

export default SearchTree;
